import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { dayOfWeek, formattedDate, time } from '@/plugins/dateFormatter'
import { formatCurrency, getGroupSettings } from '@/util/currencyFormat'

// Shared cancel flow for the transactions list and the transaction details page.
// Fetches the transaction on open to decide which choices to show, then sends
// every choice as a query flag on the cancel request.

const __default__ = { name: 'CancelTransactionDrawer' }

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  show: { type: Boolean, default: false },
  transactionId: { type: [Number, String], default: null }
},
  emits: ['update:show', 'close', 'cancelled'],
  setup(__props, { emit }) {

const props = __props





const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}`

const slotStatuses = [
  { value: 'available', label: 'Available' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'call_to_book', label: 'Call to book' }
]
const waiverOptions = [
  { value: 'keep', label: 'Keep waivers on file' },
  { value: 'delete', label: 'Delete waivers' }
]
const itemOptions = [
  { value: 'remove', label: 'Remove items' },
  { value: 'keep', label: 'Keep items' }
]
const giftCardOptions = [
  { value: 'keep', label: 'Keep gift cards active' },
  { value: 'deactivate', label: 'Deactivate gift cards' }
]

const loading = ref(false)
const submitting = ref(false)
const slotStatus = ref('available')
const waiverAction = ref('keep')
const itemAction = ref('remove')
const giftCardAction = ref('keep')
const waiverCount = ref(0)
const orderNumber = ref<string | number | null>(null)
const bookings = ref<any[]>([])
const items = ref<any[]>([])
const giftCards = ref<any[]>([])

const activeBookings = computed(() => bookings.value.filter((b: any) => b.status !== 'cancelled'))
const activeGiftCards = computed(() => giftCards.value.filter((g: any) => g.is_active === 1))
const drawerTitle = computed(() => orderNumber.value ? `Cancel transaction #${orderNumber.value}` : 'Cancel transaction')
const itemCount = computed(() => items.value.length)
const giftCardCount = computed(() => activeGiftCards.value.length)

const group = getGroupSettings()

function bookingWhen(booking: any) {
  const prefix = group.show_day_of_week ? dayOfWeek(booking.booking_date) + ', ' : ''
  const date = formattedDate(group.date_format, booking.booking_date) || ''
  if (booking.is_all_day) return prefix + date + ', ' + (booking.all_day_text || 'All day')
  const start = time(group.time_format, booking.start_time) || ''
  const end = time(group.time_format, booking.end_time) || ''
  return prefix + date + (start ? ', ' + start + (end ? ' to ' + end : '') : '')
}

function formatAmount(value: number | string) {
  return formatCurrency(value, {
    currency: group.currency,
    currencySymbol: '$',
    currencyFormat: group.currency_format,
    numberFormat: group.number_format
  })
}

const waiverHelp = computed(() => waiverAction.value === 'delete'
  ? 'Waivers are permanently removed. Time slots are clean for new bookings.'
  : 'Waivers remain attached to the time slots. Future bookings inherit them.')
const itemHelp = computed(() => itemAction.value === 'remove'
  ? 'Items are removed from the transaction and returned to inventory.'
  : 'Items stay on the transaction and their amounts remain in the total.')
const giftCardHelp = computed(() => giftCardAction.value === 'deactivate'
  ? 'Gift cards can no longer be redeemed. Balances are not refunded.'
  : 'Gift cards can still be redeemed.')

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function resetChoices() {
  slotStatus.value = 'available'
  waiverAction.value = 'keep'
  itemAction.value = 'remove'
  giftCardAction.value = 'keep'
  waiverCount.value = 0
  orderNumber.value = null
  bookings.value = []
  items.value = []
  giftCards.value = []
}

async function loadCounts() {
  if (!props.transactionId) return
  loading.value = true
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/transactions/${props.transactionId}`, config)
    const waivers = response.data.waivers || []
    waiverCount.value = waivers.length
    orderNumber.value = response.data.transaction?.order_number ?? null
    bookings.value = response.data.bookings || []
    items.value = response.data.items || []
    giftCards.value = response.data.gift_cards || []
  } catch (err) {
    console.log(err)
    // Counts stay at zero; the cancel still works with the slot status alone.
  } finally {
    loading.value = false
  }
}

async function submit() {
  if (!props.transactionId || submitting.value) return
  submitting.value = true
  try {
    const config = await authConfig()
    const url = `${baseUrl}/transactions/${props.transactionId}`
      + `?new_status=${slotStatus.value}`
      + `&delete_waivers=${waiverAction.value === 'delete'}`
      + `&remove_items=${itemAction.value === 'remove'}`
      + `&deactivate_gift_cards=${giftCardAction.value === 'deactivate'}`
    await axios.delete(url, config)
    proxy.$notify({ type: 'success', message: 'Transaction has been cancelled', icon: 'tim-icons icon-check-2' })
    emit('cancelled')
    onClose()
  } catch (err: any) {
    console.log(err)
    const message = err?.response?.status === 401
      ? 'You do not have access to this feature. Reach out to your admin regarding your site permissions.'
      : 'Something went wrong'
    proxy.$notify({ type: 'danger', message, icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    submitting.value = false
  }
}

function onClose() {
  emit('update:show', false)
  emit('close')
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape') onClose()
}

watch(() => props.show, (visible) => {
  if (visible) {
    document.addEventListener('keydown', onEscKey)
    resetChoices()
    loadCounts()
  } else {
    document.removeEventListener('keydown', onEscKey)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})

return { __sfc: true,props, emit, proxy, companyId, companyGroupId, baseUrl, slotStatuses, waiverOptions, itemOptions, giftCardOptions, loading, submitting, slotStatus, waiverAction, itemAction, giftCardAction, waiverCount, orderNumber, bookings, items, giftCards, activeBookings, activeGiftCards, drawerTitle, itemCount, giftCardCount, group, bookingWhen, formatAmount, waiverHelp, itemHelp, giftCardHelp, authConfig, resetChoices, loadCounts, submit, onClose, onEscKey }
}

})